import { useRef } from "react";
import { useInView, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import AnimatedCounter from "../components/AnimatedCounter";
import {
  containerVariants,
  titleVariants,
  textVariants,
} from "../utils/animations";

// Trust Metrics Section
const TrustMetrics = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Individual refs for each card
  const card1Ref = useRef(null);
  const card2Ref = useRef(null);
  const card3Ref = useRef(null);
  const card4Ref = useRef(null);

  // Individual inView states for each card
  const card1InView = useInView(card1Ref, { once: true, margin: "-50px" });
  const card2InView = useInView(card2Ref, { once: true, margin: "-50px" });
  const card3InView = useInView(card3Ref, { once: true, margin: "-50px" });
  const card4InView = useInView(card4Ref, { once: true, margin: "-50px" });

  // Removed pinned scroll animation for better performance

  // Animation variants for metric cards
  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.8 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
    hover: {
      scale: 1.05,
      y: -10,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const gridVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.4,
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  return (
    <section className="py-32 bg-[#F5F7FA] " ref={ref}>
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2
            className="font-playfair text-4xl lg:text-5xl font-bold text-[#C5A66A] mb-6"
            variants={titleVariants}
          >
            {t("trustMetrics.title")}
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            variants={textVariants}
          >
            {t("trustMetrics.subtitle")}
          </motion.p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={gridVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.div
            ref={card1Ref}
            className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            variants={cardVariants}
            whileHover="hover"
          >
            <AnimatedCounter end={6} shouldAnimate={card1InView} />
            <motion.p
              className="text-lg font-semibold text-[#0F1B2D] mt-2"
              initial={{ opacity: 0, y: 20 }}
              animate={
                card1InView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              {t("trustMetrics.metrics.countries.title")}
            </motion.p>
            <motion.p
              className="text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              animate={
                card1InView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.5, delay: 0.9 }}
            >
              {t("trustMetrics.metrics.countries.subtitle")}
            </motion.p>
          </motion.div>

          <motion.div
            ref={card2Ref}
            className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            variants={cardVariants}
            whileHover="hover"
          >
            <AnimatedCounter end={132} shouldAnimate={card2InView} />
            <motion.p
              className="text-lg font-semibold text-[#0F1B2D] mt-2"
              initial={{ opacity: 0, y: 20 }}
              animate={
                card2InView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.5, delay: 0.95 }}
            >
              {t("trustMetrics.metrics.projects.title")}
            </motion.p>
            <motion.p
              className="text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              animate={
                card2InView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.5, delay: 1.05 }}
            >
              {t("trustMetrics.metrics.projects.subtitle")}
            </motion.p>
          </motion.div>

          <motion.div
            ref={card3Ref}
            className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            variants={cardVariants}
            whileHover="hover"
          >
            <AnimatedCounter
              end={1.8}
              prefix="> "
              suffix=" Mrd"
              decimals={1}
              shouldAnimate={card3InView}
            />
            <motion.p
              className="text-lg font-semibold text-[#0F1B2D] mt-2"
              initial={{ opacity: 0, y: 20 }}
              animate={
                card3InView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.5, delay: 1.1 }}
            >
              {t("trustMetrics.metrics.ebit.title")}
            </motion.p>
            <motion.p
              className="text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              animate={
                card3InView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.5, delay: 1.2 }}
            >
              {t("trustMetrics.metrics.ebit.subtitle")}
            </motion.p>
          </motion.div>

          <motion.div
            ref={card4Ref}
            className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            variants={cardVariants}
            whileHover="hover"
          >
            <AnimatedCounter end={17} shouldAnimate={card4InView} />
            <motion.p
              className="text-lg font-semibold text-[#0F1B2D] mt-2"
              initial={{ opacity: 0, y: 20 }}
              animate={
                card4InView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.5, delay: 1.25 }}
            >
              {t("trustMetrics.metrics.ventures.title")}
            </motion.p>
            <motion.p
              className="text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              animate={
                card4InView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.5, delay: 1.35 }}
            >
              {t("trustMetrics.metrics.ventures.subtitle")}
            </motion.p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustMetrics;
